import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Dialog } from '../../ui';
import GrowlContainer from '../../growl/components/GrowlContainer';
import { errorGrowl, successGrowl, warningGrowl } from '../../../store/rootThunks';
import { getErrorLog, getImportStatus, importRecords } from '../api/importApi';
import log from '../../../logger';

export const IMPORT_GROWL_ID = 'importDialog';

/**
 * The CSV import dialog. Replaces `ImportCtrl` and `shared/importModal.html`.
 *
 * The flow is unchanged: pick a file, import, then poll `getImportStatus` once a second until
 * the server stops saying `pending`. Three outcomes, each with its own growl, and both error
 * outcomes offer the error log.
 *
 * Three things the Angular version got right that are easy to lose in a port:
 *
 * - **Its growls had `ttl: -1`**, so they never auto-dismissed. An import result is not a
 *   toast: the admin needs it on screen while they fetch the error log. Reproduced by passing
 *   no `ttlSeconds`, which is what `errorGrowl` already defaults to.
 * - **Closing cancels the poll.** `$scope.cancelWaitForImport` stopped the recursion; the ref
 *   here does the same, and also on unmount. Without it the loop keeps running after the
 *   dialog is gone and dispatches growls into a group nothing renders.
 * - **Cancel is disabled while importing**, because the upload is already in flight and the
 *   server will finish it either way. Closing would only hide the result.
 */
const ImportDialog = ({
  title = 'Import Records',
  instruction,
  importType,
  importParams,
  onClose,
}) => {
  const dispatch = useDispatch();
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [finished, setFinished] = useState(false);
  const [status, setStatus] = useState(undefined);
  const [importId, setImportId] = useState(undefined);
  const [hasErrors, setHasErrors] = useState(false);
  const cancelled = useRef(false);

  useEffect(
    () => () => {
      cancelled.current = true;
    },
    []
  );

  const growl = useCallback(
    (thunk, message) => dispatch(thunk({ groupId: IMPORT_GROWL_ID, props: { message } })),
    [dispatch]
  );

  const handleResult = useCallback(
    (importStatus) => {
      setImporting(false);
      setFinished(true);
      setStatus(importStatus.status);
      switch (importStatus.status) {
        case 'completed': {
          growl(successGrowl, 'Import was successful.');
          break;
        }
        case 'completed_with_errors': {
          growl(
            warningGrowl,
            'Import was successful, however some rows were invalid and not imported.'
          );
          setHasErrors(true);
          break;
        }
        default: {
          growl(errorGrowl, 'Import failed.');
          setHasErrors(true);
        }
      }
    },
    [growl]
  );

  const poll = useCallback(
    (id) => {
      setTimeout(async () => {
        if (cancelled.current) return;
        try {
          const importStatus = await getImportStatus(id);
          if (cancelled.current) return;
          if (importStatus.status === 'pending') {
            poll(id);
          } else {
            handleResult(importStatus);
          }
        } catch (error) {
          // The Angular version logged and returned a value nothing read, so a failed status
          // check silently stopped the poll and left the dialog spinning forever. Surfacing
          // it is the one deliberate change in this port.
          log.error('Failed to check import status', error);
          setImporting(false);
          growl(errorGrowl, 'Lost track of the import. Reload to see whether it completed.');
        }
      }, 1000);
    },
    [handleResult, growl]
  );

  const doImport = useCallback(async () => {
    setImporting(true);
    cancelled.current = false;
    const result = await importRecords(file, importType, importParams);
    if (cancelled.current) return;
    if (!result.success) {
      setImporting(false);
      growl(errorGrowl, result.error);
      return;
    }
    setImportId(result.importStatus.id);
    poll(result.importStatus.id);
  }, [file, importType, importParams, poll, growl]);

  const downloadErrors = useCallback(async () => {
    try {
      const errorsUrl = await getErrorLog(importId);
      window.open(errorsUrl, '_self');
    } catch (error) {
      log.error('Failed to download error log', error);
      growl(errorGrowl, 'Failed to download error log.');
    }
  }, [importId, growl]);

  const close = useCallback(() => {
    cancelled.current = true;
    onClose({ finished, status });
  }, [onClose, finished, status]);

  return (
    <Dialog
      title={title}
      onClose={close}
      busy={importing}
      footer={
        finished ? (
          <Button onClick={close}>Close</Button>
        ) : (
          <>
            <Button disabled={importing} onClick={close}>
              Cancel
            </Button>
            {/* Grey and off, saying why, until a file is chosen (SURF-4889); plainly off while
                it imports. */}
            <Button
              variant="primary"
              disabled={!file || importing}
              disabledReason={importing || file ? '' : 'Select a CSV file to import.'}
              onClick={doImport}
            >
              {importing ? 'Importing...' : 'Import'}
            </Button>
          </>
        )
      }
    >
      <GrowlContainer groupId={IMPORT_GROWL_ID} />
      {instruction && <p>{instruction}</p>}
      {!finished && (
        <div className="form-group auto-height">
          {/* A label wired to a visually-hidden input, which is how a file picker is styled
              as a button without the browser's own control. `ngf-select` did the same. */}
          <label className="btn btn-success top-left" htmlFor="importFile">
            Select file
          </label>
          <input
            id="importFile"
            type="file"
            accept=".csv,text/csv"
            className="tw-sr-only"
            onChange={(event) => setFile(event.target.files?.[0] || null)}
          />
          {file && (
            <div className="asset-preview">
              {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
            </div>
          )}
        </div>
      )}
      {hasErrors && (
        <a className="clickable" onClick={downloadErrors}>
          Download errors
        </a>
      )}
    </Dialog>
  );
};

export default ImportDialog;
